import type { TextProps as MantineTextProps } from '@mantine/core';
// eslint-disable-next-line no-restricted-imports
import { Text as MantineText, useMantineTheme } from '@mantine/core';
import { typography } from '@repo/theme/primitives';
import type { ColorNames } from '@repo/theme/utils';
import type { AriaRole, ForwardedRef } from 'react';
import { forwardRef, memo, useMemo } from 'react';

interface SecodaTextProps {
	children?: React.ReactNode;
	dangerouslySetInnerHTML?: {
		__html: string;
	};
	size?: keyof typeof typography.title | keyof typeof typography.text;
	weight?: keyof typeof typography.weight;
	color?: ColorNames;
	nonThemeColor?: string;
	role?: AriaRole;
	tabIndex?: number;
	inherit?: boolean;
}

export type TextProps = SecodaTextProps &
	Omit<MantineTextProps, keyof SecodaTextProps | 'weight'>;

export const Text = memo(
	forwardRef(
		(
			{
				children,
				inherit = false,
				size,
				weight,
				color,
				nonThemeColor,
				...others
			}: TextProps,
			ref: ForwardedRef<HTMLDivElement>
		) => {
			const theme = useMantineTheme();

			const fontSize = useMemo(() => {
				if (inherit && !size) {
					return 'inherit';
				}
				return size ? size : 'md';
			}, [inherit, size]);

			const textColor = useMemo(() => {
				if (inherit && !color) {
					return 'inherit';
				}
				return color
					? theme.other.getColor(color)
					: nonThemeColor || theme.other.getColor('text/primary/default');
			}, [inherit, color, nonThemeColor, theme]);

			const fontWeight = useMemo(() => {
				if (inherit && !weight) {
					return 'inherit';
				}
				return weight
					? typography.weight[weight]
					: typography.weight['regular'];
			}, [inherit, weight]);

			return (
				<MantineText
					ref={ref}
					size={fontSize}
					fw={fontWeight}
					color={textColor}
					inherit={inherit}
					{...others}
				>
					{children}
				</MantineText>
			);
		}
	)
);
Text.displayName = 'Text';
